import React from "react";
import { useSelector } from "react-redux";
import { constants, currencyString } from "@qgiv/core-js";
import {
    findProductFromProductId,
    getAttributesSelectedFromFieldDataId,
    getIndexOfItemAlreadyInStoreCart,
} from "@qgiv/p2p-registration";
import { useFormikContext } from "formik";
import { selectAllFormSettings } from "../../../../redux/slices/formSettingsSlice";
import {
    addItemToRegistrationStoreCart,
    removeRegistrationStoreCartItem,
    selectAllRegistration,
    updateRegistrationStoreCart,
} from "../../../../redux/slices/registrationSlice";
import {
    selectAllProductModal,
    setProductModalOpen,
    updateCurrentStoreCartItemIndex,
} from "../../../../redux/slices/productModalSlice";
import { selectCmsControlByType } from "../../../../redux/slices/cmsSettingsSlice";
import { selectIsCms } from "../../../../redux/slices/configSlice";
import { selectAllProducts } from "../../../../redux/slices/productsSlice";
import Submit from "./Submit";

const ConnectedSubmit = () => {
    const {
        ENUMS: {
            CMS_Control: { P2P_STORE_PRODUCTS },
        },
    } = constants;

    const isCms = useSelector(selectIsCms);

    const { values } = useFormikContext();
    const { Product_Id, Field_Data_Id = "", Product_Quantity } = values;

    const { currency } = useSelector(selectAllFormSettings);
    const { storeCart = [] } = useSelector(selectAllRegistration);
    const {
        currentProductId,
        modalAccessedFromStoreList = false,
        originalStoreCartItemIndex = -1,
        currentStoreCartItemIndex = -1,
    } = useSelector(selectAllProductModal);
    const products = useSelector(selectAllProducts);

    const control = useSelector((state) =>
        selectCmsControlByType(P2P_STORE_PRODUCTS, state),
    );

    const {
        options: { addButtonLabel = "Add", updateButtonLabel = "Update" },
    } = control;

    const currentProductInfo = products
        ? findProductFromProductId(Product_Id, products)
        : {};

    const indexOfItemAlreadyInStoreCart = getIndexOfItemAlreadyInStoreCart(
        storeCart,
        currentProductInfo,
        Field_Data_Id,
        Product_Id,
    );

    const isUpdateView = currentStoreCartItemIndex !== -1;

    const indexOfItemToUpdate = isUpdateView
        ? currentStoreCartItemIndex
        : indexOfItemAlreadyInStoreCart;

    // If a user has 2 Products with different attributes in the cart and
    // updates one via the change button, a change to attributes that match
    // an item already in the cart would cause the currentStoreCartItemIndex
    // to no longer be equal to the originalStoreCartItemIndex. If they are making
    // a change that would cause a duplicate, we are switching to the item in
    // the cart to update. They will see the existing cart item details and
    // be able to add to the existing.  This will mean we are no longer
    // updating the original making the current different from the original
    // The original index is store in case we need to switch back to it (they change
    // the attributes so they no longer match one existing in the cart) or
    // they submit the update and we need to remove the original because the
    // change is combined with the existing.
    const hasDuplicateItemToRemoveFromCart = !!(
        isUpdateView &&
        originalStoreCartItemIndex !== -1 &&
        currentStoreCartItemIndex !== originalStoreCartItemIndex
    );

    const indexOfItemDuplicateToRemove = hasDuplicateItemToRemoveFromCart
        ? originalStoreCartItemIndex
        : null;

    // Disable button until a selection is made for all attributes and quantity over zero.
    const hasEmptyAttributeInputs =
        currentProductInfo &&
        currentProductInfo.hasAttributes &&
        Field_Data_Id === "";
    // We also want to disable the Update button if no change in attributes or qty has been made.
    const noChangeInAttributes =
        storeCart[indexOfItemToUpdate] &&
        storeCart[indexOfItemToUpdate].Field_Data_Id === values.Field_Data_Id;
    const noChangeInQuantity =
        storeCart[indexOfItemToUpdate] &&
        storeCart[indexOfItemToUpdate].Product_Quantity ===
            values.Product_Quantity;

    const updateItemValuesHaveNotChanged =
        isUpdateView && noChangeInAttributes && noChangeInQuantity;

    const disabled = !!(
        hasEmptyAttributeInputs || updateItemValuesHaveNotChanged
    );

    const { fieldDataObject } = getAttributesSelectedFromFieldDataId(
        Field_Data_Id,
        Product_Id,
        products,
    );
    // Necessary for Accessibility
    const currentProductInfoByAttributes =
        Field_Data_Id.length > 0 && Object.keys(fieldDataObject).length > 0
            ? fieldDataObject
            : "";

    const selectedAttributes =
        Field_Data_Id.length > 0 ? currentProductInfoByAttributes.alias : "";

    const costByAttributesSelected =
        Field_Data_Id.length > 0 ? currentProductInfoByAttributes.cost : "";

    const pricePerItem =
        currentProductInfo && Field_Data_Id.length > 0
            ? currencyString(Number(costByAttributesSelected), currency)
            : currencyString(Number(currentProductInfo.cost), currency);

    const priceForAriaLabel = Product_Quantity > 1 ? "each" : "";

    // TODO:  How specific should these be? Reason for disabled button
    const standardAddButtonAriaLabel = `${addButtonLabel} ${Product_Quantity} of ${currentProductInfo.title} ${selectedAttributes}
             to cart for ${pricePerItem} ${priceForAriaLabel}`;

    const disabledAddButtonAriaLabel = `${addButtonLabel} Button Disabled : Please make a selection for all attributes`;

    const addButtonAriaLabel = !disabled
        ? standardAddButtonAriaLabel
        : disabledAddButtonAriaLabel;

    const updateButtonAriaLabel = !disabled
        ? `${updateButtonLabel} cart item to ${Product_Quantity} of ${currentProductInfo.title} ${selectedAttributes}`
        : `${updateButtonLabel} Button Disabled: Please make a change to Update item or close modal to keep item as is.`;

    const submitProps = {
        addButtonAriaLabel,
        addButtonLabel,
        currentProductId,
        disabled,
        Field_Data_Id,
        indexOfItemToUpdate,
        indexOfItemDuplicateToRemove,
        isCms,
        isUpdateView,
        modalAccessedFromStoreList,
        updateButtonAriaLabel,
        updateButtonLabel,
        //  Actions
        setProductModalOpen,
        addItemToRegistrationStoreCart,
        updateCurrentStoreCartItemIndex,
        updateRegistrationStoreCart,
        removeRegistrationStoreCartItem,
    };
    return <Submit {...submitProps} />;
};

export default ConnectedSubmit;
